import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { Box } from "@salestrak/ui";

import { InboxHeader } from "#app/components/inbox/inbox-header";
import { InboxList } from "#app/components/inbox/inbox-list";
import { useInbox } from "#app/hooks/inbox/use-inbox";
import { useInboxMutations } from "#app/hooks/inbox/use-inbox-mutations";
import { useInboxDateGroups } from "#app/hooks/inbox/use-inbox-date-groups";
import { useIssueView } from "#app/components/issue-view";
import type { IssueRow, NotificationRow as InboxStandardFragment } from "#app/lib/data";

function InboxScreen() {
  const [activeTab, setActiveTab] = useState<"all" | "cleared">("all");
  const [ordering, setOrdering] = useState<"newest" | "oldest">("newest");
  const showCleared = activeTab === "cleared";

  const { items, unreadCount, loading, loadingMore, hasMore, fetchPage, refetch } =
    useInbox({ showCleared });
  const { markRead, markCleared, markUncleared, markAllRead } =
    useInboxMutations();

  const { groups, dateKeys } = useInboxDateGroups(items, ordering);
  const { openIssue } = useIssueView();

  const handleItemPress = useCallback(
    (item: InboxStandardFragment) => {
      // Mark as read
      if (!item.read) {
        markRead([item]);
      }

      // Open the issue detail modal/sheet. A paragraph-mention notification
      // carries a customFieldId — deep-link to that field on the summary tab.
      // Otherwise fall back to the note/activity behavior.
      const issue = item.context.issue;
      if (issue) {
        const customFieldId = item.context.customFieldId ?? null;
        const target = customFieldId
          ? ({ initialTab: "summary", focusCustomFieldId: customFieldId } as const)
          : ({ initialTab: "activity", focusNoteId: item.context.noteId ?? null } as const);
        openIssue(
          {
            id: issue.id,
            issueId: issue.issueId,
            name: issue.name,
            stageId: issue.stageId,
            workflowId: issue.workflowId,
            sortOrder: 0,
            archived: false,
            noteCount: 0,
            hasUnreadNote: false,
            formCount: 0,
            issueType: issue.issueType
              ? {
                  id: issue.issueType.id,
                  issueTypeId: issue.issueType.issueTypeId,
                  name: issue.issueType.name,
                  icon: issue.issueType.icon ?? undefined,
                }
              : undefined,
            // Seed for immediate render; the issue view re-fetches full data by id.
          } as unknown as IssueRow,
          target,
        );
      }
    },
    [markRead, openIssue],
  );

  const handleClear = useCallback(
    (item: InboxStandardFragment) => {
      markCleared([item]);
    },
    [markCleared],
  );

  const handleUnclear = useCallback(
    (item: InboxStandardFragment) => {
      markUncleared([item]);
    },
    [markUncleared],
  );

  const handleClearAll = useCallback(() => {
    if (items.length > 0) {
      markCleared(items);
    }
  }, [items, markCleared]);

  return (
    <Box direction="column" fill="flex">
      <InboxHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        ordering={ordering}
        onOrderingChange={setOrdering}
        unreadCount={unreadCount}
        onMarkAllRead={markAllRead}
        onClearAll={handleClearAll}
      />
      <InboxList
        groups={groups}
        dateKeys={dateKeys}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={() => fetchPage(items.length, 50)}
        onItemClick={handleItemPress}
        onClear={handleClear}
        onUnclear={handleUnclear}
        activeTab={activeTab}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/inbox")({
  component: InboxScreen,
});
